import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { AlertDialog } from 'radix-ui'
import { Clock } from 'lucide-react'
import { Button } from '@/components/common/Button'
import { useAuth } from '@/hooks/useAuth'
import { me } from '@/api/auth'
import { IDLE_MINUTES, WARN_SECONDS, LAST_ACTIVITY_KEY, rememberSignOutReason } from '@/lib/session'

const ACTIVITY = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'] as const

/**
 * Idle sign-out with a warning (E-UX). After IDLE_MINUTES without activity in ANY tab of the app
 * a dialog counts down WARN_SECONDS; "Stay signed in" pings the API (which also keeps the server
 * session alive) and resets the clock; doing nothing signs the user out and the sign-in page says
 * why. Activity in another tab counts (shared through localStorage).
 */
export function IdleTimeout() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [warnLeft, setWarnLeft] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const lastLocal = useRef(Date.now())
  const { pathname } = useLocation()

  // Moving between pages is activity too.
  useEffect(() => {
    lastLocal.current = Date.now()
    try { localStorage.setItem(LAST_ACTIVITY_KEY, String(lastLocal.current)) } catch { /* ignore */ }
  }, [pathname])

  const markActive = useCallback(() => {
    const now = Date.now()
    // Throttle writes: once every 15 s is plenty for a minutes-scale timer.
    if (now - lastLocal.current < 15_000) return
    lastLocal.current = now
    try { localStorage.setItem(LAST_ACTIVITY_KEY, String(now)) } catch { /* storage unavailable */ }
  }, [])

  const lastActivity = () => {
    let shared = 0
    try { shared = Number(localStorage.getItem(LAST_ACTIVITY_KEY) ?? 0) } catch { /* ignore */ }
    return Math.max(lastLocal.current, shared)
  }

  const signingOut = useRef(false)
  const signOut = useCallback(async () => {
    if (signingOut.current) return // one sign-out, however many timer ticks fire
    signingOut.current = true
    rememberSignOutReason(`You were signed out after ${IDLE_MINUTES} minutes of inactivity. Please sign in again.`)
    setWarnLeft(null)
    try { await logout() } catch { /* already signed out server-side */ } finally { navigate('/login', { replace: true }) }
  }, [logout, navigate])

  useEffect(() => {
    if (!user || IDLE_MINUTES <= 0) return
    signingOut.current = false
    lastLocal.current = Date.now()
    try { localStorage.setItem(LAST_ACTIVITY_KEY, String(lastLocal.current)) } catch { /* ignore */ }
    ACTIVITY.forEach((ev) => window.addEventListener(ev, markActive, { passive: true }))
    const tick = window.setInterval(() => {
      const idleMs = Date.now() - lastActivity()
      const limit = IDLE_MINUTES * 60_000
      if (idleMs >= limit + WARN_SECONDS * 1000) { void signOut(); return }
      if (idleMs >= limit) setWarnLeft(Math.max(0, Math.ceil((limit + WARN_SECONDS * 1000 - idleMs) / 1000)))
      else setWarnLeft(null)
    }, 1000)
    return () => {
      ACTIVITY.forEach((ev) => window.removeEventListener(ev, markActive))
      window.clearInterval(tick)
    }
  }, [user, markActive, signOut])

  const stay = async () => {
    setBusy(true)
    try {
      await me() // proves the session is still alive and refreshes it server-side
      lastLocal.current = Date.now()
      try { localStorage.setItem(LAST_ACTIVITY_KEY, String(lastLocal.current)) } catch { /* ignore */ }
      setWarnLeft(null)
    } catch {
      // The server already ended the session: the API client's 401 handler takes over.
      setWarnLeft(null)
    } finally { setBusy(false) }
  }

  if (warnLeft === null) return null
  const mm = Math.floor(warnLeft / 60)
  const ss = String(warnLeft % 60).padStart(2, '0')
  return (
    <AlertDialog.Root open>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="fixed inset-0 z-[95] bg-overlay/40" />
        <AlertDialog.Content data-testid="idle-warning" className="fixed left-1/2 top-[20vh] z-[96] w-[calc(100vw-2rem)] max-w-sm -translate-x-1/2 rounded-overlay border border-border bg-card p-6 shadow-e4">
          <div className="mb-3 grid size-10 place-items-center rounded-full bg-warning-subtle text-warning-content"><Clock className="size-5" /></div>
          <AlertDialog.Title className="text-base font-semibold text-foreground">Are you still there?</AlertDialog.Title>
          <AlertDialog.Description className="mt-1.5 text-sm text-muted-foreground">
            For your security you will be signed out in <span className="num font-semibold text-foreground" aria-live="polite">{mm}:{ss}</span> because there has been no activity.
          </AlertDialog.Description>
          <div className="mt-5 flex flex-wrap justify-end gap-2">
            <Button variant="outline" onClick={() => void signOut()}>Sign out now</Button>
            <Button onClick={() => void stay()} loading={busy} autoFocus>Stay signed in</Button>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  )
}
